import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import SEO from '@/components/shared/SEO'

const CustomerReviewPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    rating: 0,
    review: '',
  })

  const [hoveredRating, setHoveredRating] = useState(0)
  const [showThankYou, setShowThankYou] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [touched, setTouched] = useState({})

  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleRatingClick = rating => {
    setFormData(prev => ({
      ...prev,
      rating,
    }))
  }

  const handleBlur = e => {
    const { name } = e.target
    setTouched(prev => ({ ...prev, [name]: true }))
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setSubmitted(true)
    if (formData.rating === 0) return

    setIsSubmitting(true)
    try {
      // Prepare data for API submission
      const submissionData = {
        web_form_id: 'claims1_customer_review',
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        rating: formData.rating,
        review: formData.review,
        submittedAt: new Date().toISOString(),
      }

      // Submit to TananTech API
      const response = await fetch(
        'https://datastore.tanantech.co.uk/1.0/data',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(submissionData),
        }
      )

      if (response.ok) {
        const result = await response.json()
        // eslint-disable-next-line no-console
        console.log('Review submitted successfully with ID:', result.dataid)

        // Show thank you popup
        setShowThankYou(true)

        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          rating: 0,
          review: '',
        })
        setTouched({})
        setSubmitted(false)
      } else {
        // eslint-disable-next-line no-console
        console.error('Failed to submit review:', response.statusText)
        alert('There was an error submitting your review. Please try again.')
        setSubmitted(false)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting review:', error)
      alert('There was an error submitting your review. Please try again.')
      setSubmitted(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Auto-close thank you popup after 20 seconds
  useEffect(() => {
    if (showThankYou) {
      const timer = setTimeout(() => {
        setShowThankYou(false)
      }, 20000) // 20 seconds

      return () => clearTimeout(timer)
    }
  }, [showThankYou])

  const renderStars = () => {
    return [...Array(5)].map((_, index) => {
      const starValue = index + 1
      return (
        <button
          key={index}
          type="button"
          className={`text-2xl transition-colors ${
            starValue <= (hoveredRating || formData.rating)
              ? 'text-orange-400'
              : 'text-gray-300'
          }`}
          onClick={() => handleRatingClick(starValue)}
          onMouseEnter={() => setHoveredRating(starValue)}
          onMouseLeave={() => setHoveredRating(0)}
        >
          â˜…
        </button>
      )
    })
  }

  return (
    <>
      <SEO
        title="Share Your Experience - Customer Reviews | Your Claim Matters"
        description="Share your experience with Your Claim Matters car finance claims service. Leave a review to help other customers and help us improve our service."
        keywords="Your Claim Matters reviews, customer feedback, car finance claims reviews, customer experience, testimonials"
        url="/customer-review"
        image="/Image/Car-photo-illustration-claims1.webp"
        imageAlt="Your Claim Matters customer reviews and feedback"
        schemaType="WebPage"
        schemaData={{
          mainEntity: {
            '@type': 'WebPage',
            name: 'Customer Review Form',
            description:
              'Submit your review and feedback about Your Claim Matters car finance claims service',
            publisher: {
              '@type': 'Organization',
              name: 'Your Claim Matters',
              url: 'https://yourclaimmatters.co.uk',
            },
          },
        }}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Customer Reviews', url: '/customer-review' },
        ]}
      />
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* Header Section */}
          <div className="mb-12 text-center">
            <h1 className="mb-4 text-4xl font-black tracking-tight text-gray-900 uppercase md:text-5xl">
              SHARE YOUR EXPERIENCE
            </h1>
            <p className="mx-auto max-w-2xl text-lg leading-relaxed text-gray-600">
              Help other customers by sharing your experience with Your Claim
              Matters.
              <br />
              Your feedback is valuable to us and helps us improve our service.
            </p>
          </div>

          {/* Main Content */}
          <div className="mx-auto max-w-6xl">
            <div className="overflow-hidden rounded-lg bg-white shadow-lg">
              <div className="grid md:grid-cols-2">
                {/* Left Side - Gradient Section */}
                <div
                  className="flex flex-col justify-center p-8 text-white md:p-12"
                  style={{
                    background:
                      'linear-gradient(to bottom right, #095678 0%, #0d86bc 70%, #ff914d 100%)',
                  }}
                >
                  <h2 className="mb-6 text-3xl font-bold md:text-4xl">
                    Share Your Feedback
                  </h2>
                  <p className="text-lg leading-relaxed text-white">
                    Your review helps other customers understand our service and
                    helps us continue to improve. Share your experience with
                    Your Claim Matters.
                  </p>
                </div>

                {/* Right Side - Form */}
                <div className="p-8 md:p-12">
                  <h3 className="mb-8 text-2xl font-bold text-gray-900">
                    Leave a Review
                  </h3>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name Fields */}
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label
                          htmlFor="firstName"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          First Name*
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          required
                          className="w-full rounded-md border border-gray-300 px-4 py-3 transition-colors focus:border-transparent focus:ring-2 focus:ring-[#095678]"
                        />
                        {touched.firstName && !formData.firstName && (
                          <p className="mt-1 text-sm text-red-500">
                            First name is required
                          </p>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="lastName"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          Last Name*
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          required
                          className="w-full rounded-md border border-gray-300 px-4 py-3 transition-colors focus:border-transparent focus:ring-2 focus:ring-[#095678]"
                        />
                        {touched.lastName && !formData.lastName && (
                          <p className="mt-1 text-sm text-red-500">
                            Last name is required
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Email Field */}
                    <div>
                      <label
                        htmlFor="email"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        Email Address*
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        required
                        className="w-full rounded-md border border-gray-300 px-4 py-3 transition-colors focus:border-transparent focus:ring-2 focus:ring-[#095678]"
                      />
                      {touched.email && !formData.email && (
                        <p className="mt-1 text-sm text-red-500">
                          Email address is required
                        </p>
                      )}
                    </div>

                    {/* Rating Field */}
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Rating*
                      </label>
                      <div className="mb-2 flex items-center gap-1">
                        {renderStars()}
                      </div>
                      {submitted && formData.rating === 0 && (
                        <p className="text-sm text-red-500">Rating required</p>
                      )}
                    </div>

                    {/* Review Field */}
                    <div>
                      <label
                        htmlFor="review"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        Your Review*
                      </label>
                      <textarea
                        id="review"
                        name="review"
                        value={formData.review}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        required
                        rows={6}
                        maxLength={500}
                        placeholder="Tell us about your experience with Your Claim Matters..."
                        className="sm:resize-vertical w-full resize-none rounded-md border border-gray-300 px-4 py-3 transition-colors focus:border-transparent focus:ring-2 focus:ring-[#095678]"
                      />
                      <div className="mt-1 flex items-center justify-between">
                        <div></div>
                        <p className="text-sm text-gray-500">
                          {formData.review.length}/500 characters maximum
                        </p>
                      </div>
                    </div>

                    {touched.review && !formData.review && (
                      <p className="mt-1 text-sm text-red-500">
                        Review is required
                      </p>
                    )}

                    {/* Submit Button */}
                    <div className="pt-4 text-center">
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="h-14 px-8 text-xl font-black disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <svg
                              className="h-5 w-5 animate-spin"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                              />
                            </svg>
                            Submitting...
                          </span>
                        ) : (
                          'Submit Review'
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Thank You Popup */}
        {showThankYou && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="mx-4 max-w-md rounded-lg bg-white p-8 text-center shadow-2xl">
              <div className="mb-4">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100">
                  <svg
                    className="h-8 w-8 text-[#ff914d]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                </div>
                <h3 className="mb-2 text-2xl font-bold text-gray-900">
                  Thank You!
                </h3>
                <p className="mb-6 text-gray-600">
                  Your review has been submitted successfully. We appreciate
                  your feedback and it helps us improve our service.
                </p>
                <Button
                  onClick={() => setShowThankYou(false)}
                  className="h-14 px-8 text-xl font-black"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default CustomerReviewPage
